import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ShortEncryptorService {
  // Define standard character set (lowercase alphanumeric)
  private readonly alphabet: string = 'abcdefghijklmnopqrstuvwxyz0123456789';
  private readonly base: number = this.alphabet.length;
  
  // A secret key multiplier (must be a prime number)
  private readonly secretPrime: number = 4013;

  constructor() {}

  /**
   * Generates a deterministic shuffled alphabet based on a salt seed
   */
  private getShuffledAlphabet(seed: number): string {
    const chars = this.alphabet.split('');
    let currentSeed = seed * this.secretPrime;
    
    for (let i = chars.length - 1; i > 0; i--) {
      // Linear Congruential Generator algorithm for shuffle predictability
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = currentSeed % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  }

  /**
   * Encrypts a short lowercase word into a randomized 5-6 character string.
   * @param word String to encrypt (must use characters existing in a-z or 0-9)
   */
  public encrypt(word: string): string {
    if (!word) return '';

    // 1. Pick a random Salt Index between 0 and 9
    const saltIndex = Math.floor(Math.random() * 10);
    
    // 2. Generate a custom alphabet unique to this specific salt execution
    const dynamicAlphabet = this.getShuffledAlphabet(saltIndex);
    
    // 3. Perform a character-by-character shifted transformation
    let cipherText = '';
    for (let i = 0; i < word.length; i++) {
      const originalPos = this.alphabet.indexOf(word[i].toLowerCase());
      if (originalPos === -1) continue; // Skip unsupported characters
      
      const newPos = (originalPos + i + saltIndex) % this.base;
      cipherText += dynamicAlphabet[newPos];
    }

    // 4. Append the salt index token as the final character
    const retVal = `${cipherText}${saltIndex}`;
    return retVal.toUpperCase();
  }

  /**
   * Decrypts the short code back to its original unified token format.
   * @param cipherText The encrypted token string generated by this service
   */
  public decrypt(cipherText: string): string {
    if (!cipherText || cipherText.length < 2) return '';

    cipherText = cipherText.toLowerCase();

    // 1. Extract the salt index configuration tag out of the terminal character position
    const saltIndex = parseInt(cipherText.slice(-1), 10);
    const cleanCipher = cipherText.slice(0, -1);
    
    if (isNaN(saltIndex)) return '';

    // 2. Reconstruct the identical matching dynamic array alphabet mapping
    const dynamicAlphabet = this.getShuffledAlphabet(saltIndex);
    
    let plainText = '';
    for (let i = 0; i < cleanCipher.length; i++) {
      const cipherPos = dynamicAlphabet.indexOf(cleanCipher[i]);
      if (cipherPos === -1) continue;

      // Reverse the positioning shifts applied during encryption execution
      let originalPos = (cipherPos - i - saltIndex) % this.base;
      if (originalPos < 0) originalPos += this.base;
      
      plainText += this.alphabet[originalPos];
    }
    
    return plainText;
  }
}
